// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Currency } from '@/currency/api/types';
import { Project } from '@/project/api/types';
import { ProjectChart } from '@/report/components/project-chart';
import { useProjectReportChart } from '@/report/hooks/use-project-report-chart';
import { colors } from '@mui/material';

// The compact center label rounds, so it takes the exact total as a number.
function formatCompactTotal(total: Currency): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: total.isoCode,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(total.amount.toNumber());
}

interface Props {
  width: number;
  height: number;
  projects: Project[];
  groupCount: number;
  emptyState: string;
  onHover: (id: string | null, color: string | null) => void;
}

export function ProjectTotalContractValueChart(props: Props) {
  const { data, contractValueTotals, isEmpty, getValue, getColor } =
    useProjectReportChart(props.projects, {
      count: props.groupCount,
      metric: 'totalContractValue',
      colors: [colors.blue[800], colors.blue[100]],
    });
  const isChartEmpty = isEmpty || props.projects.length === 0;
  const emptyState =
    isEmpty && props.projects.length > 0
      ? 'No project value data for the upcoming projects'
      : props.emptyState;

  const handleHover = (id: string | null) => {
    if (id === null) {
      return props.onHover(null, null);
    }

    const index = data.findIndex((datum) => datum.id === id);
    const color = getColor(id, index);

    props.onHover(id, color);
  };

  return (
    <ProjectChart
      data={data}
      width={props.width}
      height={props.height}
      onHover={handleHover}
      isEmpty={isChartEmpty}
      emptyState={emptyState}
      getValue={getValue}
      getColor={getColor}
    >
      {contractValueTotals.map(formatCompactTotal)}
    </ProjectChart>
  );
}
